import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mountain, Upload, Trash2, Loader2, Layers3, Calculator, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { listSurfaces, createSurface, deleteSurface, computeVolume } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";

const ROLE_COLORS = { existing: "text-emerald-400 border-emerald-500/30", design: "text-purple-400 border-purple-500/30", other: "text-zinc-400 border-zinc-700" };

export default function SurfacesPage() {
  const [surfaces, setSurfaces] = useState([]);
  const [name, setName] = useState("");
  const [role, setRole] = useState("existing");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [picked, setPicked] = useState(null);

  const refresh = () => listSurfaces().then(setSurfaces);
  useEffect(() => { refresh(); }, []);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPicked(f);
    if (!name) setName(f.name.replace(/\.[^.]+$/, ""));
  };

  const upload = async () => {
    if (!picked) { toast.error("Choose a TIN file (.xml LandXML, .xyz/.csv, .12da, .dxf)"); return; }
    setBusy(true);
    try {
      const content = await picked.text();
      const s = await createSurface(name || picked.name, role, picked.name, content);
      toast.success(`Imported "${s.name}" — ${s.point_count} pts, ${s.face_count} faces`);
      setPicked(null); setName(""); if (fileRef.current) fileRef.current.value = "";
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not parse surface");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => { await deleteSurface(id); refresh(); toast.success("Surface removed"); };

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-8 sticky top-0 bg-[#0A0A0A]/85 backdrop-blur z-10">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">Reference Surfaces (TIN)</h1>
          <p className="font-mono text-[11px] text-zinc-500">Import terrain models to source surface levels — reference only, never written to ADAC output</p>
        </div>
      </header>

      <div className="p-6 max-w-4xl space-y-6">
        <div className="border border-zinc-800 rounded-sm bg-[#101010] p-4 space-y-3" data-testid="surface-upload">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-zinc-500">
            <Upload className="w-3.5 h-3.5" /> Import surface
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept=".xml,.xyz,.csv,.txt,.12da,.dxf" onChange={onPick} data-testid="surface-file" className="font-mono text-xs text-zinc-400 file:mr-3 file:rounded-sm file:border-0 file:bg-blue-600 file:text-white file:px-3 file:py-1.5 file:font-mono file:text-xs" />
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Surface name" data-testid="surface-name" className="h-9 w-48 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs" />
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="surface-role" className="h-9 w-36 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
                <SelectItem value="existing" className="font-mono text-xs">Existing ground</SelectItem>
                <SelectItem value="design" className="font-mono text-xs">Design surface</SelectItem>
                <SelectItem value="other" className="font-mono text-xs">Other</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={upload} disabled={busy} data-testid="surface-upload-btn" className="h-9 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Import
            </Button>
          </div>
          <p className="font-mono text-[10px] text-zinc-600">Supported: LandXML (.xml) full TIN · XYZ / CSV points · 12d (.12da) · DXF 3DFACE. Easting/Northing auto-detected (AU MGA).</p>
        </div>

        <div className="space-y-2" data-testid="surface-list">
          {surfaces.length === 0 ? (
            <div className="grid-texture border border-zinc-800 rounded-sm py-16 flex flex-col items-center text-center">
              <Mountain className="w-10 h-10 text-zinc-700 mb-3" />
              <p className="text-sm text-zinc-500">No surfaces yet. Import a TIN to auto-source surface levels in the Profile &amp; cross-sections.</p>
            </div>
          ) : surfaces.map((s) => (
            <div key={s.id} data-testid={`surface-${s.id}`} className="border border-zinc-800 rounded-sm bg-[#101010] p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Layers3 className="w-5 h-5 text-zinc-500" />
                <div>
                  <div className="font-mono text-sm text-zinc-200">{s.name} <span className={`ml-2 px-1.5 py-0.5 rounded-sm border text-[10px] uppercase ${ROLE_COLORS[s.role] || ROLE_COLORS.other}`}>{s.role}</span></div>
                  <div className="font-mono text-[10px] text-zinc-500">{s.format.toUpperCase()} · {s.point_count} pts · {s.face_count} faces · E {s.bounds?.emin?.toFixed(0)}–{s.bounds?.emax?.toFixed(0)} · Z {s.bounds?.zmin?.toFixed(2)}–{s.bounds?.zmax?.toFixed(2)}</div>
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`delete-surface-${s.id}`} className="h-8 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-zinc-900"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>

        <VolumesPanel surfaces={surfaces} />
      </div>
    </div>
  );
}

function VolumesPanel({ surfaces }) {
  const [base, setBase] = useState("");
  const [top, setTop] = useState("");
  const [spacing, setSpacing] = useState(1);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const { activeProject } = useProject();

  useEffect(() => {
    if (!activeProject) return;
    if (activeProject.existing_surface_id) setBase(activeProject.existing_surface_id);
    if (activeProject.design_surface_id) setTop(activeProject.design_surface_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id]);

  const compute = async () => {
    if (!base || !top) { toast.error("Pick two surfaces"); return; }
    if (base === top) { toast.error("Pick two different surfaces"); return; }
    setBusy(true);
    try {
      const r = await computeVolume(base, top, parseFloat(spacing) || 1);
      setRes(r);
      toast.success(`Cut ${r.cut_m3} m³ · Fill ${r.fill_m3} m³`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Volume calculation failed");
      setRes(null);
    } finally {
      setBusy(false);
    }
  };

  const exportCSV = () => {
    if (!res) return;
    const rows = [["TIN-to-TIN Volume Report"], ["Base (lower) surface", res.base_name], ["Top (upper) surface", res.top_name],
      ["Cut (m3)", res.cut_m3], ["Fill (m3)", res.fill_m3], ["Net (m3)", res.net_m3], ["Balance", res.balance],
      ["Plan area analysed (m2)", res.area_m2], ["Grid spacing (m)", res.spacing], ["Cells", res.cells]];
    const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = `Volume_${res.base_name}_to_${res.top_name}.csv`.replace(/\s+/g, "_"); a.click();
    URL.revokeObjectURL(url);
  };

  const opts = surfaces.map((s) => (
    <SelectItem key={s.id} value={s.id} className="font-mono text-[11px]">{s.name} ({s.role})</SelectItem>
  ));

  return (
    <div className="border border-zinc-800 rounded-sm bg-[#101010] p-4 space-y-3" data-testid="volumes-panel">
      <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-zinc-500">
        <Calculator className="w-3.5 h-3.5" /> TIN-to-TIN volumes (cut / fill)
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="font-mono text-[10px] text-zinc-500">Base (lower / existing)</label>
          <Select value={base} onValueChange={setBase}>
            <SelectTrigger data-testid="vol-base" className="w-52 h-9 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]"><SelectValue placeholder="Base surface…" /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-64">{opts}</SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="font-mono text-[10px] text-zinc-500">Top (upper / design)</label>
          <Select value={top} onValueChange={setTop}>
            <SelectTrigger data-testid="vol-top" className="w-52 h-9 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]"><SelectValue placeholder="Top surface…" /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-64">{opts}</SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="font-mono text-[10px] text-zinc-500">Grid (m)</label>
          <Input type="number" step="0.5" value={spacing} onChange={(e) => setSpacing(e.target.value)} data-testid="vol-spacing" className="w-24 h-9 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-[11px]" />
        </div>
        <Button onClick={compute} disabled={busy} data-testid="vol-compute-btn" className="h-9 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />} Compute
        </Button>
        {res && (
          <Button onClick={exportCSV} variant="outline" data-testid="vol-csv-btn" className="h-9 rounded-sm border-zinc-700 bg-transparent text-zinc-300">
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
        )}
      </div>
      <p className="font-mono text-[10px] text-zinc-600">Grid (prism) method over the plan overlap of both surfaces. Fill = top above base; Cut = top below base. Spacing auto-coarsens for very large extents.</p>

      {res && (
        <div className="space-y-3" data-testid="volumes-result">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <VolStat label="Cut" value={`${res.cut_m3.toLocaleString()} m³`} color="text-red-400" testid="vol-cut" />
            <VolStat label="Fill" value={`${res.fill_m3.toLocaleString()} m³`} color="text-blue-400" testid="vol-fill" />
            <VolStat label={`Net (${res.balance})`} value={`${res.net_m3.toLocaleString()} m³`} color={res.net_m3 >= 0 ? "text-blue-400" : "text-red-400"} testid="vol-net" />
            <VolStat label="Plan area" value={`${res.area_m2.toLocaleString()} m²`} color="text-zinc-200" testid="vol-area" />
          </div>
          <div className="font-mono text-[10px] text-zinc-500">{res.base_name} → {res.top_name} · {res.cells.toLocaleString()} cells @ {res.spacing} m · E {res.bounds.emin}–{res.bounds.emax} · N {res.bounds.nmin}–{res.bounds.nmax}</div>
          <VolumeHeatmap res={res} />
        </div>
      )}
    </div>
  );
}

function VolStat({ label, value, color, testid }) {
  return (
    <div className="border border-zinc-800 rounded-sm bg-[#0C0C0C] p-3" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className={`font-display text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}

function VolumeHeatmap({ res }) {
  const { nx, ny, diffs, dmin, dmax } = res;
  const cell = Math.max(2, Math.min(10, Math.floor(520 / nx)));
  const W = nx * cell, H = ny * cell;
  const absMax = Math.max(Math.abs(dmin || 0), Math.abs(dmax || 0)) || 1;
  const colorOf = (d) => {
    if (d == null) return "#0a0a0a";
    const t = Math.max(-1, Math.min(1, d / absMax));
    if (t >= 0) { // fill -> blue
      const a = 0.15 + 0.65 * t;
      return `rgba(59,130,246,${a.toFixed(2)})`;
    }
    const a = 0.15 + 0.65 * -t; // cut -> red
    return `rgba(239,68,68,${a.toFixed(2)})`;
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-3 font-mono text-[10px] text-zinc-500">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "rgba(239,68,68,0.8)" }} /> Cut</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: "rgba(59,130,246,0.8)" }} /> Fill</span>
        <span>Δ range {dmin}…{dmax} m (plan view)</span>
      </div>
      <div className="border border-zinc-800 rounded-sm bg-[#0C0C0C] overflow-auto p-2" data-testid="volume-heatmap" style={{ maxHeight: "50vh" }}>
        <svg width={W} height={H} style={{ display: "block" }}>
          {diffs.map((d, i) => {
            const ix = i % nx, iy = Math.floor(i / nx);
            return <rect key={i} x={ix * cell} y={(ny - 1 - iy) * cell} width={cell} height={cell} fill={colorOf(d)} />;
          })}
        </svg>
      </div>
    </div>
  );
}
